import { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { startCase } from "lodash";
import {
  Modal,
  ModalDialog,
  ModalOverflow,
  Typography,
  Stack,
  Divider,
  Button,
} from "@financeable-com-au/financeable-ui";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { DOCUMENT_TEMPLATE_TYPE, ERRORS_MESSAGE, REFERRAL_FEE } from "@/constants";
import {
  applicationSelector,
  userDetailsSelector,
  saveValue,
  updateLoanDetails,
  getApplicationDocumentPreview,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type {
  ApplicationFormSlice,
  ApplicationState,
  UserDetailsState,
} from "@/store/slices/types/applicationFormSlice";
import { RepaymentFrequency } from "@/store/slices/types/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { useTemplateServices } from "@/hooks";
import { IDispatchResponse } from "@/utils/interface";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "@/components/Utils/helperFunction";
import { roundToTwoDecimals } from "@/utils/formatNumber";

interface DisclosureStatementModalProps {
  isCustomQuote: boolean;
}

const modalWidth = "480px";

const DisclosureStatementModal = ({ isCustomQuote }: DisclosureStatementModalProps) => {
  const dispatch = useDispatch();
  const { canUseDisclosureServices } = useTemplateServices();
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const {
    product,
    showDisclosureStatement,
    setShowDisclosureStatement,
    setShowQuoteCalculator,
    setShowCriteria,
    lenderCard,
    lenderCardCustom,
    inArrears,
  } = useLenderCardContext();
  const { disclosureLoader } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const isDisabledDisclosure = !canUseDisclosureServices || disclosureLoader;

  const disclosureInfo = {
    referrer: lenderCardCustom?.referrer,
    inArrears,

    ...(isCustomQuote
      ? {
          ratePercentage: lenderCardCustom?.rate?.default?.formattedValue,
          rateValue: lenderCardCustom?.rate?.default?.value,
          loanAmount: lenderCardCustom?.loanAmount?.formattedValue,
          loanAmountValue: lenderCardCustom?.loanAmount?.value,
          financeAmount: lenderCardCustom?.financeAmount?.formattedValue,
          financeAmountValue: lenderCardCustom?.financeAmount?.value,
          effectiveRatePercentage: lenderCardCustom?.rate?.effective?.formattedValue,
          effectiveRateValue: lenderCardCustom?.rate?.effective?.value,
          fees: lenderCardCustom?.fees?.application,
          formattedRepayments: lenderCardCustom?.repayments?.primary?.formattedValue,
          repaymentsValue: lenderCardCustom?.repayments?.primary?.value,
          repaymentFrequency: lenderCardCustom?.repaymentFrequency,
          brokerage: lenderCardCustom?.brokerage?.percentage?.value,
          brokerageAmount: lenderCardCustom?.brokerage?.amount?.value,
        }
      : {
          ratePercentage: lenderCard?.rate?.default?.formattedValue,
          rateValue: lenderCard?.rate?.default?.value,
          loanAmount: lenderCard?.loanAmount?.formattedValue,
          loanAmountValue: lenderCard?.loanAmount?.value,
          financeAmount: lenderCard?.financeAmount?.formattedValue,
          financeAmountValue: lenderCard?.financeAmount?.value,
          effectiveRatePercentage: lenderCard?.rate?.effective?.formattedValue,
          effectiveRateValue: lenderCard?.rate?.effective?.value,
          fees: lenderCard?.fees?.application,
          formattedRepayments: lenderCard?.repayments?.monthly?.formattedValue,
          repaymentsValue: lenderCard?.repayments?.monthly?.value,
          repaymentFrequency: RepaymentFrequency.Monthly,
          brokerage: lenderCard?.brokerage?.percentage?.value,
          brokerageAmount: lenderCard?.brokerage?.amount?.value,
        }),
  };

  const handleSendDisclosure = () => {
    const customerId = application?.customers?.[0]._id;

    if (isDisabledDisclosure) return;

    if (!customerId) {
      dispatch(
        openSnackbar({
          message: `Please add a ${getCustomerRole(application.applicationType, application.entities, application.purpose)} to the application`,
          intent: "danger",
        }),
      );
      return;
    }

    const disclosurePayload = {
      customerId,
      applicationId: application._id,
      templateType: DOCUMENT_TEMPLATE_TYPE.CUSTOMER_DISCLOSURE,
      disclosureInfo: {
        rate: disclosureInfo.rateValue,
        loanAmount: disclosureInfo.loanAmountValue,
        financeAmount: disclosureInfo.financeAmountValue,
        repayments: roundToTwoDecimals(disclosureInfo.repaymentsValue),
        repaymentFrequency: disclosureInfo.repaymentFrequency,
        effectiveRate: roundToTwoDecimals(disclosureInfo.effectiveRateValue),
        fees: disclosureInfo.fees,
        referrer: disclosureInfo.referrer,
        lender: product?.lender ?? "",
        productName: product?.productName,
        productType: product?.productType,
        inArrears: disclosureInfo.inArrears,
        brokerage: roundToTwoDecimals(disclosureInfo.brokerage),
        brokerageAmount: roundToTwoDecimals(disclosureInfo.brokerageAmount),
      },
    };
    return disclosurePayload;
  };

  const handle = {
    handlePreviewDocument: async () => {
      const getMappedDisclosurePayload = handleSendDisclosure();
      dispatch(saveValue({ disclosureLoader: true }));

      const loanDetailsId = application?.loanDetails?._id;

      if (loanDetailsId) {
        await dispatch(
          updateLoanDetails({
            ...getMappedDisclosurePayload?.disclosureInfo,
            applicationId: application?._id,
            _id: loanDetailsId,
          }),
        ).unwrap();
      }

      dispatch(getApplicationDocumentPreview(getMappedDisclosurePayload)).then((res: IDispatchResponse) => {
        handleClose();
        if (res.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          // close disclosure modal and open preview document modal
          dispatch(
            saveValue({
              disclosureLoader: false,
              showPreviewDocument: {
                isOpen: true,
                customerId: getMappedDisclosurePayload?.customerId,
                templateType: getMappedDisclosurePayload?.templateType,
                disclosureInfo: getMappedDisclosurePayload?.disclosureInfo,
              },
            }),
          );
        }
      });
    },
  };

  const handleClose = () => {
    setShowDisclosureStatement(false);
  };

  return (
    <Modal
      aria-labelledby="modal-title"
      aria-describedby="modal-desc"
      open={!!showDisclosureStatement}
      onClose={handleClose}
    >
      <ModalOverflow>
        <ModalDialog minWidth={modalWidth} maxWidth={modalWidth}>
          <Stack direction="column" rowGap={6}>
            <Stack direction="column" rowGap={2}>
              <Typography component="h3" level="title-md" id="modal-title">
                Send Proposal Disclosure Statement
              </Typography>
              <Typography component="p" id="modal-desc">
                Please review the details below
              </Typography>
              <Divider />
            </Stack>

            <Stack sx={{ position: "relative" }} flexGrow={1}>
              <Stack
                flexDirection="column"
                rowGap={2}
                sx={{
                  opacity: disclosureLoader ? 0.4 : 1,
                  transition: "opacity 0.2s ease",
                }}
              >
                <Stack flexDirection="row">
                  <Typography component="span" level="body-sm" bold>
                    Loan Amount:&nbsp;
                  </Typography>
                  <Typography component="span" level="body-sm">
                    {disclosureInfo.loanAmount}
                  </Typography>
                </Stack>
                <Stack flexDirection="row">
                  <Typography component="span" level="body-sm" bold>
                    Repayment term:&nbsp;
                  </Typography>
                  <Typography component="span" level="body-sm">
                    {application.loanDetails.term} months
                  </Typography>
                </Stack>
                <Stack flexDirection="row">
                  <Typography component="span" level="body-sm" bold>
                    Rate:&nbsp;
                  </Typography>
                  <Typography component="span" level="body-sm">
                    {disclosureInfo.ratePercentage}
                  </Typography>
                </Stack>
                <Stack flexDirection="row">
                  <Typography component="span" level="body-sm" bold>
                    Effective Rate:&nbsp;
                  </Typography>
                  <Typography component="span" level="body-sm">
                    {disclosureInfo.effectiveRatePercentage}
                  </Typography>
                </Stack>
                {disclosureInfo &&
                  disclosureInfo.fees?.length > 0 &&
                  disclosureInfo.fees.map(
                    ({ name, formattedValue, key }: { name: string; formattedValue: string; key: string }) => {
                      const isReferrer = key === REFERRAL_FEE;
                      return (
                        <Fragment key={key}>
                          <Stack flexDirection="row">
                            <Typography component="span" level="body-sm" bold>
                              {startCase(name)}:&nbsp;
                            </Typography>
                            <Typography component="span" level="body-sm">
                              {formattedValue}
                            </Typography>
                          </Stack>
                          {isReferrer && disclosureInfo.referrer && (
                            <Stack flexDirection="row">
                              <Typography component="span" level="body-sm" bold>
                                Referrer:&nbsp;
                              </Typography>
                              <Typography component="span" level="body-sm">
                                {disclosureInfo.referrer}
                              </Typography>
                            </Stack>
                          )}
                        </Fragment>
                      );
                    },
                  )}
                <Stack flexDirection="row">
                  <Typography component="span" level="body-sm" bold>
                    In Arrears:&nbsp;
                  </Typography>
                  <Typography component="span" level="body-sm">
                    {disclosureInfo.inArrears}
                  </Typography>
                </Stack>
                <Stack flexDirection="row">
                  <Typography component="span" level="body-sm" bold>
                    Repayments:&nbsp;
                  </Typography>
                  <Typography component="span" level="body-sm">
                    {disclosureInfo.formattedRepayments}
                  </Typography>
                </Stack>
              </Stack>
              {disclosureLoader && (
                <Stack
                  sx={{
                    position: "absolute",
                    inset: 0,
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <CircularProgress size={32} />
                </Stack>
              )}
            </Stack>
            <Stack direction="row" columnGap={2} width="100%" justifyContent="space-between">
              <Stack direction="row" columnGap={2}>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowDisclosureStatement(false);
                    setShowCriteria(false);
                    setShowQuoteCalculator(true);
                  }}
                >
                  Customise
                </Button>
              </Stack>
              <Button variant="primary" onClick={handle.handlePreviewDocument} disabled={isDisabledDisclosure}>
                {disclosureLoader ? "Getting document..." : "Preview document"}
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export { DisclosureStatementModal };
